import React, { FunctionComponent, memo, useCallback } from 'react'
import { useCombobox, UseComboboxGetItemPropsOptions } from 'downshift'
import debounce from 'lodash/debounce'
import './Autocomplete.scss'
import { useScrollToInputOnVirtualKeyboardOpened } from '@hooks/useScrollToInputOnVirtualKeyboardOpened'

export interface KeyValue {
  value: string
  label: string
}

interface ItemProps {
  isHighlighted: boolean
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  getItemProps: (options: UseComboboxGetItemPropsOptions<KeyValue>) => any
  item: KeyValue
  index: number
}

const ItemComponent: FunctionComponent<ItemProps> = ({ isHighlighted, getItemProps, item, index }) => {
  const className = isHighlighted ? 'highlighted' : ''
  return (
    <li className={className} key={item.value} {...getItemProps({ item, index })}>
      {item.label}
    </li>
  )
}

const Item = memo(ItemComponent)

interface Props {
  placeholder: string
  items: KeyValue[]
  defaultSelectedItem?: KeyValue
  onInputChange: (input: string) => void | Promise<void>
  onSelectChange: (item: KeyValue) => void | Promise<void>
}

const itemToString = (item: KeyValue | null): string => item?.label ?? ''

const Autocomplete: FunctionComponent<Props> = ({
  placeholder,
  items,
  defaultSelectedItem,
  onInputChange,
  onSelectChange,
}) => {
  const { scrollToRef } = useScrollToInputOnVirtualKeyboardOpened()

  const onInputValueChange = useCallback(
    debounce(({ inputValue }) => {
      if (inputValue) {
        onInputChange(inputValue)
      }
    }, 500),
    [onInputChange],
  )

  const onSelectedItemChange = useCallback(
    debounce(({ selectedItem }) => {
      if (selectedItem) {
        onSelectChange(selectedItem)
      }
    }, 500),
    [onSelectChange],
  )

  const { isOpen, getMenuProps, getInputProps, getComboboxProps, getItemProps, selectedItem } = useCombobox({
    items,
    itemToString,
    onSelectedItemChange,
    onInputValueChange,
    defaultSelectedItem,
  })

  return (
    <div ref={scrollToRef as any} className="autocomplete">
      <div className="combo" {...getComboboxProps()}>
        <input placeholder={placeholder} name="recipient" {...getInputProps()} id="recipient" />
      </div>
      <ul {...getMenuProps()} className="menu">
        {isOpen &&
          items.map((item, index) => (
            <Item
              key={item.value}
              isHighlighted={selectedItem?.value === item.value}
              getItemProps={getItemProps}
              item={item}
              index={index}
            />
          ))}
      </ul>
    </div>
  )
}

export default Autocomplete
